import React, { useState, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { getSchedules, getScheduleSlots } from "../../api/Schedule";
import { useSelector } from "react-redux";
import { computeServiceCategoryAvailabilities } from "../../../utils/ScheduleAvailabilitySupport";

const ScheduleOverview = () => {
  const [events, setEvents] = useState([]);
  const [schedules, setSchedules] = useState([]); 
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("");
  const [practitioners, setPractitioners] = useState([]);
  const [practitionerFilter, setPractitionerFilter] = useState("");
  const loggedInUserData = useSelector((state) => state?.auth?.user);
  const calendarRef = useRef(null);

  // Fetch schedules and store them
  const fetchSchedules = async (startDateParam, endDateParam) => {
    // Convert to ISO for API
    const startISO = startDateParam.toISOString();
    const endISO = endDateParam.toISOString();

    const schedulesResponse = await getSchedules(
      loggedInUserData["custom:unique_id"],
      loggedInUserData["custom:orgId"],
      startISO,
      endISO
    );
    const fetchedSchedules = schedulesResponse.data || [];

    // For each schedule, fetch its slots
    const schedulesWithSlots = await Promise.all(
      fetchedSchedules.map(async (schedule) => {
        const slotsResponse = await getScheduleSlots(schedule.id, null,loggedInUserData["custom:orgId"] );
        const slots = slotsResponse.data || [];
        return { ...schedule, slots };
      })
    );

    setSchedules(schedulesWithSlots);
  };

  // processSchedules: Build availability for each serviceCategory only on its date
  const processSchedules = () => {
    if (!schedules.length) {
      setEvents([]);
      return;
    }

    const allServiceCategoriesTemp = {};
    const practitionersList = [];

    // Collect all unique practitioners
    for (const schedule of schedules) {
      schedule.actor?.forEach((actor) => {
        if (!practitionersList.find((p) => p.reference === actor.reference)) {
          practitionersList.push(actor);
        }
      });
    }
    setPractitioners(practitionersList.sort((a, b) => (a.display > b.display) ? 1 : -1));

    // Filter schedules by practitioner if needed
    const filteredSchedules = practitionerFilter
      ? schedules.filter((schedule) =>
        schedule.actor?.some(
          (actor) => actor.reference === practitionerFilter
        )
      )
      : schedules;

    for (const schedule of filteredSchedules) {
      (schedule.serviceCategory || []).forEach((sc) => {
        if (sc?.id && !allServiceCategoriesTemp[sc.id]) {
          allServiceCategoriesTemp[sc.id] = { id: sc.id, name: sc.name };
        }
      });

      (schedule.slots || []).forEach((slot) => {
        (slot.serviceCategory || []).forEach((item) => {
          const id = item?.id || item?.coding?.[0]?.code;
          const name = item?.name || item?.coding?.[0]?.display;
          if (id && !allServiceCategoriesTemp[id]) {
            allServiceCategoriesTemp[id] = { id, name };
          }
        });
      });
    }

    const serviceCategoryAvailabilities = computeServiceCategoryAvailabilities(filteredSchedules);
    const mergedEvents = [];
    for (const scId in serviceCategoryAvailabilities) {
      const categoryName = allServiceCategoriesTemp[scId]?.name || "Unknown";

      serviceCategoryAvailabilities[scId].forEach((period) => {
        mergedEvents.push({
          start: period.start,
          end: period.end,
          category: scId,
          categoryName: categoryName,
        });
      });
    }

    setServiceCategories(Object.values(allServiceCategoriesTemp).sort((a, b) => (a.name > b.name) ? 1 : -1));
    setEvents(mergedEvents);
  };

  // On mount, use FullCalendar's visible range to load schedules for the current week
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const { activeStart, activeEnd } = calendarApi.view;
      // This ensures the data load corresponds exactly to the current visible range
      fetchSchedules(new Date(activeStart), new Date(activeEnd));
    }
  }, []);

  // Process schedules whenever schedules or practitionerFilter changes
  useEffect(() => {
    processSchedules();
  }, [schedules, practitionerFilter]);

  // Handler for reloading data based on calendar's visible range
  const handleReloadData = () => {
    const calendarApi = calendarRef.current.getApi();
    const view = calendarApi.view;
    const startDateParam = new Date(view.activeStart);
    const endDateParam = new Date(view.activeEnd);

    fetchSchedules(startDateParam, endDateParam);
  };

  // Apply service category filter
  const filteredEvents = events.filter((event) => {
    return serviceCategoryFilter
      ? event.category === serviceCategoryFilter
      : true;
  });

  return (
    <div style={{ margin: "20px" }}>
      {/* Add custom CSS for styling every other slot */}
      <style>
        {`
          /* Style for even slots */
          .even-slot {
            background-color: #f7f7f7; /* Your desired background color */
          }
          /* Style for the legend square */
          .legend-square {
            width: 40px;
            height: 40px;
            background-color: #3788d8;
            display: inline-block;
            margin-right: 5px;
          }
          .legend-item {
            display: flex;
            align-items: center;
            margin-right: 15px;
          }
          .legend-container {
            display: flex;
            align-items: center;
          }
        `}
      </style>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "20px",
          justifyContent: "space-between",
        }}
      >
        {/* Filters on the left */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: "20px" }}>
            <label htmlFor="serviceCategoryFilter" style={{ marginRight: "10px" }}>
              Filter by Service Category:
            </label>
            <select
              id="serviceCategoryFilter"
              value={serviceCategoryFilter}
              onChange={(e) => setServiceCategoryFilter(e.target.value)}
            >
              <option value="">All</option>
              {serviceCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginRight: "20px" }}>
            <label htmlFor="practitionerFilter" style={{ marginRight: "10px" }}>
              Filter by Practitioner:
            </label>
            <select
              id="practitionerFilter"
              value={practitionerFilter}
              onChange={(e) => setPractitionerFilter(e.target.value)}
            >
              <option value="">All</option>
              {practitioners.map((practitioner) => (
                <option key={practitioner.reference} value={practitioner.reference}>
                  {practitioner.display}
                </option>
              ))}
            </select>
          </div>
          <button onClick={handleReloadData}>Reload Data for Visible Range</button>
        </div>

        {/* Legend aligned to the right */}
        <div className="legend-container">
          <div className="legend-item">
            <div className="legend-square" />
            <span>Available</span>
          </div>
          {/* You can add more legend items here if needed */}
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin]}
          initialView="timeGridWeek"
          // Ensure slot duration matches calculations
          slotDuration="00:30:00"
          events={filteredEvents}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,timeGridDay",
          }}
          slotLabelClassNames={(arg) => {
            const totalMinutes =
              arg.date.getHours() * 60 + arg.date.getMinutes();
            const slotIndex = totalMinutes / 30; // 30 from slotDuration
            if (Math.floor(slotIndex) % 2 === 0) {
              // Even slot
              return ["even-slot"];
            } else {
              return [];
            }
          }}
          slotLaneClassNames={(arg) => {
            const totalMinutes =
              arg.date.getHours() * 60 + arg.date.getMinutes();
            const slotIndex = totalMinutes / 30; // 30 from slotDuration
            if (Math.floor(slotIndex) % 2 === 0) {
              // Even slot
              return ["even-slot"];
            } else {
              return [];
            }
          }}
          eventContent={(eventInfo) => {
            const start = eventInfo.event.start;
            const end = eventInfo.event.end;
            if (!start || !end) {
              return <div>Time not available</div>;
            }

            const startTime = start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
            const endTime = end.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
            const categoryName = eventInfo.event.extendedProps.categoryName;

            return (
              <div>
                <div>{`${startTime} - ${endTime}`}</div>
                <div style={{ fontWeight: "bold" }}>{categoryName}</div>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default ScheduleOverview;
